import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { optimisticallyUpdateSyncStatusToRunning } from "~api/sync.ts";
import { tenantKeys } from "~api/tenants";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext";
import { ControllingProjectsService } from "~generated";

// Lives apart from projects.ts because the header's sync menu must not pull the projects journal into the start graph.
// Stays under controllingProjectKeys.base so invalidating the base still refreshes the sync status.
export const controllingSyncStatusKey = (tenantId: string) =>
  [
    ...tenantKeys.detail(tenantId),
    "controllingProjects",
    "v1",
    "syncStatus",
  ] as const;

export function useControllingDataSyncStatus({
  enabled,
}: {
  enabled: boolean;
}) {
  const currentTenant = useCurrentTenant();

  return useQuery({
    queryKey: controllingSyncStatusKey(currentTenant.id),
    queryFn: async () =>
      ControllingProjectsService.getControllingDataSyncStatus(currentTenant.id),
    refetchInterval: 5000,
    enabled,
  });
}

export function useSyncControllingData() {
  const currentTenant = useCurrentTenant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () =>
      ControllingProjectsService.syncControllingData(currentTenant.id),
    onMutate: () => {
      optimisticallyUpdateSyncStatusToRunning({
        queryKey: controllingSyncStatusKey(currentTenant.id),
        queryClient,
      });
    },
  });
}
